import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  GREY,
  BASE,
  BORDER,
  BRAND,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import CustomModal from "../../../../../components/Modals/CustomModal";

/**
 * BillingChangePlanModal
 *
 * Confirmation dialog shown BEFORE a plan change is committed, so the user clearly understands
 * what will happen (upgrade = immediate + credits reset now; downgrade = takes effect at period
 * end; buy = redirect to checkout). Prevents accidental one-click plan changes.
 *
 * `pendingChange` is produced by classifyPlanChange(): { action, title, message, confirmLabel }.
 */
const BillingChangePlanModal = memo(
  ({ open, onClose, pendingChange, isBusy, onConfirm }) => {
    const action = pendingChange?.action || "buy";
    const isDowngrade = action === "downgrade";
    const icon = isDowngrade ? ICON_LABELS.SCHEDULE : ICON_LABELS.TRENDING_UP;
    const noteLabel =
      action === "upgrade"
        ? "Takes effect immediately"
        : isDowngrade
          ? "Takes effect at the end of your current billing period"
          : "You'll continue to secure checkout";

    return (
      <CustomModal
        open={open}
        close={onClose}
        header={{
          heading: {
            id: "change_plan",
            label: pendingChange?.title || "Change plan",
          },
        }}
        width="500px"
        footer={{
          left: (
            <Box style={{ width: "fit-content" }}>
              <Button
                label="Go back"
                variant="ghost"
                size="sm36"
                onClick={onClose}
                disabled={isBusy}
              />
            </Box>
          ),
          right: (
            <Box style={{ width: "fit-content" }}>
              <Button
                label={pendingChange?.confirmLabel || "Confirm"}
                variant="primary"
                size="sm36"
                onClick={onConfirm}
                isLoading={isBusy}
                disabled={isBusy}
              />
            </Box>
          ),
        }}
      >
        <Box>
          <Box
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              padding: "14px 16px",
              background: PRIMARY.W_50,
              borderRadius: "8px",
              border: `1px solid ${PRIMARY.W_200}`,
            }}
          >
            <IconComponent
              iconLabel={icon}
              style={{ color: BRAND.PRIMARY, fontSize: "22px", marginTop: "1px" }}
            />
            <Box>
              <TypographyInline
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label={noteLabel}
              />
              <Box style={{ marginTop: "6px" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label={pendingChange?.message || ""}
                />
              </Box>
            </Box>
          </Box>

          <Box
            style={{
              marginTop: "14px",
              padding: "12px 14px",
              background: BASE.BACKGROUND_LIGHT,
              borderRadius: "8px",
              border: `1px solid ${BORDER.LIGHT}`,
            }}
          >
            <TypographyInline
              size="xs"
              color={GREY.TERTIARY}
              label="You can review your plan any time under Billing & Subscription."
            />
          </Box>
        </Box>
      </CustomModal>
    );
  },
);

BillingChangePlanModal.displayName = "BillingChangePlanModal";

export default BillingChangePlanModal;
